/**
 * useNodeContext Hook
 *
 * Calculates the available context for a given node in a workflow.
 * Uses the NodeTypeSchema system to determine what entities (conversation, contact, lead, ticket)
 * are available at any point in the workflow based on predecessor nodes.
 */

import { useMemo } from 'react';
import { Node, Edge } from 'reactflow';
import type { WorkflowNodeType } from '@troia-v3/types';
import {
  type AvailableContext,
  type ConditionalOption,
  type ContextEntityKey,
  type NodeTypeSchema,
  calculateAvailableContext,
  getAvailableOptions,
  validateNodePlacement,
  NODE_TYPE_SCHEMAS,
} from '../utils';

/**
 * Result of context calculation for a node
 */
export interface NodeContextResult {
  /** Available context at this node position */
  availableContext: AvailableContext;
  /** Conditional options that are available based on context */
  availableOptions: ConditionalOption[];
  /** All conditional options for this node type (including unavailable) */
  allOptions: ConditionalOption[];
  /** Validation result for this node placement */
  validation: {
    isValid: boolean;
    missingContext: string[];
  };
  /** The schema for this node type */
  schema: NodeTypeSchema | null;
}

/**
 * Convert React Flow nodes to the format expected by calculateAvailableContext
 */
export const convertNodes = (
  flowNodes: Node[]
): Array<{ id: string; type: WorkflowNodeType; data?: { config?: Record<string, unknown> } }> => {
  return flowNodes.map((node) => ({
    id: node.id,
    type: (node.data?.type || node.type) as WorkflowNodeType,
    data: {
      config: node.data?.config,
    },
  }));
};

/**
 * Convert React Flow edges to the format expected by calculateAvailableContext
 * Excludes "tools" edges as they represent tool connections, not flow connections
 */
export const convertEdges = (flowEdges: Edge[]): Array<{ source: string; target: string; sourceHandle?: string }> => {
  return flowEdges
    .filter((edge) => edge.sourceHandle !== 'tools') // Exclude tools connections
    .map((edge) => ({
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle || undefined,
    }));
};

/**
 * Hook to calculate available context for a specific node
 *
 * @param nodes - All nodes in the workflow
 * @param edges - All edges in the workflow
 * @param selectedNodeId - ID of the node to calculate context for
 * @returns Context calculation result
 *
 * @example
 * ```tsx
 * const { availableContext, availableOptions } = useNodeContext(nodes, edges, selectedNode.id);
 *
 * // Check if conversation is available
 * if (availableContext.conversation) {
 *   // Show "Current Conversation" option
 * }
 *
 * // Get available options for action_send_message
 * availableOptions.forEach(option => {
 *   // Render option in UI
 * });
 * ```
 */
export function useNodeContext(
  nodes: Node[],
  edges: Edge[],
  selectedNodeId: string | null
): NodeContextResult {
  return useMemo(() => {
    // Default empty result
    const emptyResult: NodeContextResult = {
      availableContext: {
        conversation: false,
        contact: false,
        lead: false,
        ticket: false,
        ai: false,
      },
      availableOptions: [],
      allOptions: [],
      validation: {
        isValid: true,
        missingContext: [],
      },
      schema: null,
    };

    if (!selectedNodeId || nodes.length === 0) {
      return emptyResult;
    }

    // Find selected node
    const selectedNode = nodes.find((n) => n.id === selectedNodeId);
    if (!selectedNode) {
      return emptyResult;
    }

    const nodeType = (selectedNode.data?.type || selectedNode.type) as WorkflowNodeType;

    // Get schema for this node type
    const schema = NODE_TYPE_SCHEMAS[nodeType] || null;

    // Convert to expected format
    const convertedNodes = convertNodes(nodes);
    const convertedEdges = convertEdges(edges);

    // Calculate available context at this node position
    const availableContext = calculateAvailableContext(
      convertedNodes,
      convertedEdges,
      selectedNodeId
    );

    // Get available options based on context
    const availableOptions = getAvailableOptions(nodeType, availableContext);

    // Get all options for this node type
    const allOptions = schema?.conditionalOptions || [];

    // Validate node placement
    const validation = validateNodePlacement(nodeType, availableContext);

    return {
      availableContext,
      availableOptions,
      allOptions,
      validation,
      schema,
    };
  }, [nodes, edges, selectedNodeId]);
}

/**
 * Hook to check if a specific context entity is available
 *
 * @param nodes - All nodes in the workflow
 * @param edges - All edges in the workflow
 * @param selectedNodeId - ID of the node to check
 * @param contextKey - The context key to check (conversation, contact, lead, ticket)
 * @returns boolean indicating if the context is available
 */
export function useHasContext(
  nodes: Node[],
  edges: Edge[],
  selectedNodeId: string | null,
  contextKey: ContextEntityKey
): boolean {
  const { availableContext } = useNodeContext(nodes, edges, selectedNodeId);
  return availableContext[contextKey] ?? false;
}

/**
 * Hook to get the schema for a node type
 *
 * @param nodeType - The node type to get schema for
 * @returns The schema or null if not found
 */
export function useNodeSchema(nodeType: string | null): NodeTypeSchema | null {
  return useMemo(() => {
    if (!nodeType) return null;
    return NODE_TYPE_SCHEMAS[nodeType as WorkflowNodeType] || null;
  }, [nodeType]);
}

export default useNodeContext;
